import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import MoreContentRequester from 'components/MoreContentRequester';
import AccessGuard from 'components/shared/AccessGuard';
import ActivityInput from 'components/shared/activityInput/ActivityInput';
import { ActivityInputValues, defaultActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import Checkbox from 'components/shared/form/Checkbox';
import { useProfile } from 'context/profile';
import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import { PERMISSIONS } from 'models/Profile';
import Workflow, { WorkflowAction } from 'models/Workflow';
import { taggableUsersOnWorkflowAction } from 'services/api/taggableUsers';
import {
  useCreateWorkflowActionActivity,
  useWorkflowActionActivities,
  workflowActionActivityQueryKeys,
} from 'services/api/workflowActionActivities';
import { workflowQueryKeys } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import NoteRow, { DRAWER_PADDING, noteRowId } from './NoteRow';
import SkeletonNoteList from './SkeletonNoteList';

// How far a mention link will page forward looking for its note.
//
// A guard that should never fire: a clinical assessment gets a few notes, so a mention link lands
// on page 1. It is here only so a bug cannot turn one arrival into an unbounded run of fetches.
const MAX_FORWARD_PAGES = 5;

// How long the arrival highlight holds at full strength before it fades. The fade itself is a CSS
// transition on NoteRow.
const FOCUS_HOLD_MS = 2000;

/**
 * The Notes tab on a clinical assessment slideout.
 *
 * The thread is its own call rather than part of the workflow show payload — embedding it
 * would cost extra queries on every referral fetch for data that may never be viewed.
 *
 * The composer's draft is owned by the caller, not by this component: the drawer swaps tabs by
 * conditional render, so state held here would be thrown away by a trip to the assessment tab
 * and back — losing a typed-but-unposted note with no warning.
 */
export default function NotesTab({
  workflowId,
  action,
  groupCount,
  values,
  setValues,
  sendToAll,
  setSendToAll,
  focusActivityId,
  onFocusHandled,
}: {
  workflowId: string;
  action: WorkflowAction;
  /** How many clinical assessments on this referral a fanned-out note can reach: the ones the
   * reader can see AND that settled with criteria, since that result is how a thread is opened.
   * Gates the fan-out checkbox — with one there is nothing to fan out to. */
  groupCount: number;
  values: ActivityInputValues;
  setValues: Dispatch<SetStateAction<ActivityInputValues>>;
  /** Owned by the caller for the reason the draft is: this tab unmounts on a trip to the
   * Assessment tab, which would otherwise silently drop a checked box. */
  sendToAll: boolean;
  setSendToAll: Dispatch<SetStateAction<boolean>>;
  /** The note a mention email pointed at, to be paged forward to and scrolled into view. */
  focusActivityId?: string | null;
  /** Called once the note has been reached, so the arrival is not replayed on a later mount. */
  onFocusHandled?: () => void;
}) {
  const { profile } = useProfile();

  // Loads the current thread every time the tab opens. Live updates land separately.
  const activitiesQuery = useWorkflowActionActivities(workflowId, action.id);
  const { mutate, isPending: isPosting } = useCreateWorkflowActionActivity(workflowId, action.id);

  const queryClient = useQueryClient();

  /**
   * Live updates for the open thread. Scoped to this tab's mount rather than the slideout's
   * open state: the tab unmounts when you switch to Assessment, and the thread query is
   * refetchOnMount:'always', so re-opening it converges anyway. Closing the drawer unmounts
   * this too, so nothing arrives while the slideout is shut.
   *
   * No debounce, unlike useWorkflowNudge — that one collapses bursts from a settling run.
   * Notes are hand-typed one at a time.
   */
  useWebsocketSubscription<{ id: string; activities?: boolean; actorId: string | null }>({
    channel: 'WorkflowActionChannel',
    params: { workflow_action_id: action.id },
    onReceived: (data) => {
      // Your own note is already on screen — the create mutation put it there.
      if (data.actorId === profile?.id) return;
      // The subscribe-time transmit is { id, actorId } with no association key.
      if (!data.activities) return;
      // This thread's key only. The shared activities helper is a prefix invalidation
      // that would take the patient story feed with it.
      queryClient.invalidateQueries({ queryKey: workflowActionActivityQueryKeys.thread(workflowId, action.id) });
    },
  });

  // The API's own answer, from the last page it served: reading the thread is what changes this,
  // so its response is where the new value comes from. Undefined against an api that predates the
  // field, which is what the fallback below is for.
  const pages = activitiesQuery.data?.pages;
  const unreadPerApi = pages?.[pages.length - 1]?.meta.hasNewActivities;

  /**
   * Move the unread indicator to match what the reader has actually been shown.
   *
   * The API answers this on every page it serves, having just stamped that page read, so the flag
   * is applied in both directions — a later page holding notes nobody has shown this reader keeps
   * the icon lit, and a history they went through weeks ago does not.
   *
   * Only the API can answer it. Its flag is "an unarchived note by someone else with no view stamp
   * for me", and from here a page that was never fetched is indistinguishable from one read on an
   * earlier visit. Deriving it locally therefore gets one of those two cases wrong, and since the
   * thread is newest-first, "one new note on a long read history" is the ordinary case.
   *
   * The fallback covers an api without the field, so the two repos deploy in either order: clear
   * only when every page has been fetched, which is the one case the client can be sure of. It
   * never raises the flag — a partially-read thread stays as it is until a workflow read settles
   * it, which is exactly the behaviour that shipped before the field existed.
   *
   * `isSuccess` is what keeps a failed fetch out of all of this. On an error there is no data, so
   * `isFetching` and `hasNextPage` are both false — the reader would get "Notes could not be
   * loaded." and lose the icon on a thread they were never shown, with nothing stamped read on the
   * API side to bring it back. Queries do not retry (`services/http.ts`), so one failure is
   * enough. Same for a query paused offline.
   */
  useEffect(() => {
    if (!activitiesQuery.isSuccess || activitiesQuery.isFetching) return;

    if (unreadPerApi === undefined) {
      if (activitiesQuery.hasNextPage) return;

      queryClient.setQueryData<Workflow>(
        workflowQueryKeys.show(workflowId),
        (prev) => prev?.withReadActivities(action.id) ?? prev
      );
      return;
    }

    queryClient.setQueryData<Workflow>(workflowQueryKeys.show(workflowId), (prev) =>
      unreadPerApi ? (prev?.withUnreadActivities(action.id) ?? prev) : (prev?.withReadActivities(action.id) ?? prev)
    );
  }, [
    activitiesQuery.isSuccess,
    activitiesQuery.isFetching,
    activitiesQuery.hasNextPage,
    unreadPerApi,
    action.id,
    workflowId,
    queryClient,
  ]);

  const notes = useMemo(() => activitiesQuery.data?.pages.flatMap((page) => page.data) ?? [], [activitiesQuery.data]);

  const listRef = useRef<HTMLDivElement>(null);

  /**
   * Arriving from a mention email: find the note the reader was tagged in and scroll it in,
   * paging forward if it is further back than the first 25.
   *
   * Not found: the tab stays open, unscrolled. Never an error state — the reader is on the right
   * thread either way. Archiving is not one of these cases; an archived note stays in the thread,
   * struck through, so a link to one still lands on it.
   *
   * Settling reports back — reached it, or ran out of thread looking — and the caller stops passing
   * it. The refs below are the guard within one mount; this is the guard across mounts, which is
   * what a trip to the Assessment tab and back is: the drawer swaps tabs by conditional render, so
   * without it the second visit re-pages the thread and drags the reader back to a note they have
   * already read. Giving up has to report back for the same reason — an id that matches nothing
   * would otherwise re-run the whole forward search on every visit for as long as the drawer
   * stays open.
   *
   * No API surface for this, deliberately: an `?around=` anchor param would be exact but adds a
   * second contract to cover a case this covers.
   */
  const pagesRequested = useRef(0);
  const scrolledTo = useRef<string | null>(null);
  const { isFetching, isError, hasNextPage, fetchNextPage } = activitiesQuery;

  // Which note is wearing the arrival highlight. State rather than the param itself, because the
  // mark is temporary while the param is not — the reader keeps the link in their URL.
  const [markedNoteId, setMarkedNoteId] = useState<string | null>(null);

  useEffect(() => {
    if (!focusActivityId || isFetching || scrolledTo.current === focusActivityId) return;

    if (notes.some((note) => note.id === focusActivityId)) {
      scrolledTo.current = focusActivityId;
      setMarkedNoteId(focusActivityId);
      document.getElementById(noteRowId(focusActivityId))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onFocusHandled?.();
      return;
    }

    if (hasNextPage && pagesRequested.current < MAX_FORWARD_PAGES) {
      pagesRequested.current += 1;
      fetchNextPage();
      return;
    }

    // A failed fetch is not an answer about the note: there is no data, so `hasNextPage` is false
    // for a reason that has nothing to do with the thread's length. Leave the arrival unspent so
    // re-opening the tab, which refetches, gets another go at it.
    if (isError) return;

    // The end of the thread, or the bound. The note is not coming, so mark the arrival spent
    // rather than leaving it to be retried by the next mount.
    scrolledTo.current = focusActivityId;
    onFocusHandled?.();
  }, [focusActivityId, notes, isFetching, isError, hasNextPage, fetchNextPage, onFocusHandled]);

  /**
   * Long enough to be seen, then gone — the mark says "this one" on arrival, it is not permanent
   * chrome. NoteRow transitions its tint, so dropping the flag fades rather than snaps.
   *
   * The clock starts when the note actually lands on screen rather than on mount: a note on page 3
   * takes a few fetches to reach, and a timer started at mount would have burnt its hold by then.
   */
  useEffect(() => {
    if (!markedNoteId) return;

    const timer = setTimeout(() => setMarkedNoteId(null), FOCUS_HOLD_MS);

    return () => clearTimeout(timer);
  }, [markedNoteId]);

  // A tag names one facility's staff, so a fanned-out copy could not carry it — the checkbox is
  // locked while there is one in the draft, and the hint beside it says why.
  const isTagging = values.mentions.length > 0;

  // Both reasons the box cannot be touched, so the greyed label tracks the lock rather than only
  // one of the two cases that set it.
  const isFanOutLocked = isPosting || isTagging;

  /**
   * Clear a box that was already checked when the tag went in.
   *
   * Locking it alone is not enough: the author can check the box first and tag second, which would
   * leave the note going to every facility with the control that says so frozen out of reach. The
   * tag is the later decision, so it wins, and the box reverts to the single facility the thread is
   * on. Removing the tag leaves it cleared rather than restoring the check — where the note goes is
   * the author's call, not something to guess back for them.
   */
  useEffect(() => {
    if (isTagging && sendToAll) setSendToAll(false);
  }, [isTagging, sendToAll, setSendToAll]);

  const post = () =>
    mutate(
      {
        text: values.text,
        plaintext: values.plaintext,
        mentions: values.mentions,
        // Spread rather than `sendToAllGroups: sendToAll`, so an unchecked post is byte-identical to the
        // request this endpoint already serves.
        ...(sendToAll && { sendToAllGroups: true }),
      },
      {
        onSuccess: () => {
          setValues(defaultActivityInputValues);
          setSendToAll(false);
          // The new note lands at the top of the thread, so follow it there — otherwise posting
          // from halfway down a long thread looks like nothing happened.
          listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        },
      }
    );

  return (
    <Container>
      <Heading>Assessment Notes</Heading>
      <Subcopy>
        Document clinical reasoning, flag decisions, and any context your team should know before admission.
      </Subcopy>

      {/* Writing a note is one capability wherever the note hangs, so this asks what the patient
          story and the referral slideout ask, and matches what the API enforces: the POST runs
          through `Activity::NotePolicy#create?`. The action's own permissions govern the action's
          controls, not its thread. A view-only admin's role carries no note permission at all, so
          the tab reads without a composer. */}
      <AccessGuard accessor={PERMISSIONS.activityNoteCreate}>
        <ComposerSlot>
          <ActivityInput
            showPostButton
            canPost
            postLabel='Post Note'
            // Documents belong to the referral packet, not to a note on one facility's assessment.
            // A dragged file cannot get in either — it is refused with a toast rather than ignored.
            attachable={false}
            taggable
            mentionFontWeight={500}
            // Fixed at the mock's 120px, so the composer never grows: `ComposerSlot` does not
            // shrink, so a growing composer pushed the thread out of the drawer. Equal min and max
            // scroll a long draft inside the box instead, leaving the thread where it was.
            heightSettings={{ minHeight: '120px', maxHeight: '120px' }}
            taggableUserResource={taggableUsersOnWorkflowAction(action.id)}
            placeholder='Start typing here to add a note for additional context for the team'
            values={values}
            setValues={setValues}
            submitting={isPosting}
            onSubmit={post}
            // Only when there is somewhere to fan out to. The count gates the affordance; the
            // server recomputes the authoritative set and writes no fan-out keys for a lone target.
            actionsSlot={
              groupCount > 1 ? (
                <FanOutActions>
                  {/* A fanned-out note reaches facilities whose staff the tag list here does not
                      cover, so a tagged note cannot go to all of them. Said only once there is a
                      tag to lose — with none typed it is a rule about nothing — and it arrives with
                      the lock on the box, so the control never goes dead without a reason. */}
                  {isTagging && <FanOutHint>Note: Tags can’t be sent to all facilities</FanOutHint>}
                  <SendToAllSlot>
                    <Checkbox
                      checked={sendToAll}
                      size={14}
                      labelSize='12px'
                      label='Send to all facilities'
                      dataCyLabel='sendToAll'
                      // Greys the label with the box. Checkbox fades only the icon when disabled,
                      // and it colours the label from this prop, so the pair reads as one dead
                      // control instead of a live label beside a faded box. black25 is the tint the
                      // repo's other disabled controls use.
                      color={isFanOutLocked ? colors.black25 : colors.black100}
                      // Locked while the post is in flight, like the textarea and the button beside
                      // it. A toggle landing mid-flight cannot change where the note goes — the
                      // request has already been sent — and onSuccess clears the box, so leaving it
                      // live would swallow the click without saying so.
                      //
                      // Locked again while the draft carries a tag, which the fan-out copies cannot
                      // carry: the hint above appears with the lock, so the two arrive together.
                      disabled={isFanOutLocked}
                      onChange={() => setSendToAll((prev) => !prev)}
                    />
                  </SendToAllSlot>
                </FanOutActions>
              ) : undefined
            }
          />
        </ComposerSlot>
      </AccessGuard>

      {activitiesQuery.isLoading ? (
        <List>
          <SkeletonNoteList />
        </List>
      ) : /* Must come before the empty state. On error `isPending` is false, so `isLoading` is
            false and `notes` is [] — a failed fetch would otherwise read as an authoritative
            "this facility has no notes". */
      activitiesQuery.isError ? (
        <ErrorSlot data-testid='notes-error'>
          <ErrorText>Notes could not be loaded.</ErrorText>
        </ErrorSlot>
      ) : notes.length === 0 ? null : (
        <List data-testid='notes-list' ref={listRef}>
          {notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              workflowId={workflowId}
              actionId={action.id}
              // Both sides can be absent — Activity defaults `createdByUser` to `{}` and the
              // profile context to `{} as Profile` — and `undefined === undefined` would offer a
              // Delete the API then rejects. Authorship has to be positively established.
              isAuthor={!!profile?.id && note.data.createdByUser?.id === profile.id}
              // Marks the note a mention link pointed at, for FOCUS_HOLD_MS from the moment it
              // lands on screen. Long enough to say "this one" on arrival, then gone — a reader
              // who scrolls away and back is reading the thread, not still looking for it.
              focused={note.id === markedNoteId}
            />
          ))}
          {/* Pulls the next page into view on scroll, like the patient story feed. The
              endpoint pages at 25, so a long thread would otherwise stop dead at note 25. */}
          <MoreContentRequester resource={activitiesQuery} />
        </List>
      )}
    </Container>
  );
}

// Only the note list scrolls. The heading, the blurb and the composer stay put, so the place
// you type never slides off screen while you read back through a long thread.
//
// No `gap` here on purpose: each child owns its own spacing, so the numbers below are the
// numbers on screen rather than something you have to add a container gap to.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const Heading = styled(BodySmallBold)`
  margin-bottom: 4px;
  font-size: 14px;
  color: ${colors.black100};
  flex-shrink: 0;
`;

const Subcopy = styled(BodySmall)`
  margin-bottom: 16px;
  font-size: 12px;
  color: ${colors.black50};
  flex-shrink: 0;
`;

// The composer keeps its natural height rather than being squeezed by a long thread.
const ComposerSlot = styled.div`
  flex-shrink: 0;

  /* The shared composer's Post button is 10px/14px, tuned for the patient story feed. The
     notes mock wants a larger target. Scoped here rather than changed in ActivityInput, so
     the patient story and the task modal keep the sizing they already ship. */
  [data-testid='activity-input-post'] {
    height: 32px;
    font-size: 12px;
    line-height: 1;

    /* FanOutActions' auto margin claims the bar's free space to push the group right. This button
       ships an auto left margin of its own, and two auto margins in one flex row SPLIT the free
       space rather than the first taking it — which strands the checkbox mid-bar. Zeroed here
       rather than in ActivityInput, whose button is shared with the patient story. */
    margin-left: 0;
  }
`;

// `margin-left: auto` here rather than on ActivityInput's SubmitButton, which carries its own and
// is shared with every other composer in the app. This slot absorbs the bar's free space first, so
// the button's auto-margin has none left to take and the group ends up adjacent to it on the right
// — the checkbox reads as attached to the action it modifies instead of drifting over to the tag
// icon, and the hint stays beside the checkbox it is about.
const FanOutActions = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  gap: 16px;
`;

// Small, at the size of the checkbox label it sits beside, but full black: it is the reason the
// control next to it has just gone dead, so it has to be read rather than skimmed past.
const FanOutHint = styled(BodySmall)`
  font-size: 12px;
  color: ${colors.black100};
`;

// The same border-and-padding treatment the patient story's Escalate / Mark as priority controls
// carry in this bar (PriorityEscalationCheckbox.tsx:47-54), so the two read as one family.
// Reimplemented rather than imported: that component's AccessGuard needs a locationEpisode and an
// episode, and it writes into ActivityInputValues, which is shared with the patient story.
const SendToAllSlot = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  /* On the RIGHT, mirroring the patient story's left-hand rule rather than copying it. There the
     checkboxes sit against the Tag/Attach icons, so a left border divides them from those; here the
     auto margin has moved this to the far end of the bar, where a left border floats in empty space
     and the only neighbour to separate from is Post Note. Padding mirrored with it, so the gap
     either side of the rule matches what the patient story has. */
  border-right: 1px solid var(--black-10);
  padding: 4px 16px 4px 0px;
  gap: 10px;
`;

// `min-height: 0` lets this shrink below its content height, which is what confines the
// scrollbar here instead of letting it escape to the drawer.
//
// Margin rather than padding on top, so it stays a gap under the composer instead of becoming
// scrollable space above the first note. Notes are now bordered cards (NoteRow) rather than
// full-bleed dividered rows, so the list owns the 24px between them via `gap` as well.
//
// Horizontally it bleeds out to the drawer's edges and pads its content back — the same trick
// DrawerTabs uses. `overflow-y: auto` computes `overflow-x` to `auto`, so anything reaching past
// its padding box would be clipped instead. The scrollbar rides the panel edge as a result.
const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;
  min-height: 0;
  margin: 24px -${DRAWER_PADDING}px 0;
  padding: 0 ${DRAWER_PADDING}px;
  overflow-y: auto;
`;

// The empty state is short, so it just centres in the leftover space. Same 24px below the
// composer as the note list gets.
const EmptySlot = styled.div`
  flex: 1;
  min-height: 0;
  margin-top: 24px;
  overflow-y: auto;
`;

// Deliberately quieter than the empty state and without its icon, so the two are not mistaken
// for each other at a glance — this one says we failed, not that there is nothing here.
const ErrorSlot = styled(EmptySlot)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 64px 24px;
  text-align: center;
`;

const ErrorText = styled(BodySmall)`
  font-size: 13px;
  color: ${colors.black50};
`;
